<template>
    <div class="nav-menu gap-2 md:gap-7 lg:gap-12 2xl:gap-20">
        <!-- Video AI -->
        <el-popover
            placement="bottom"
            trigger="hover"
            popper-class="video-pop"
            :show-arrow="false"
            :popper-style="popStyle"
        >
            <template #reference>
                <NuxtLink to="/ai-video" class="nav-link">
                    {{ t('menus.videoAI') }}
                    <svg
                        class="w-4 h-4 ml-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </NuxtLink>
            </template>
            <div class="dropdown-content">
                <div class="features-section">
                    <NuxtLink to="/image-to-video" class="feature-item">
                        <div class="feature-icon">
                            <svg
                                class="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div class="feature-content_">
                            <div class="feature-title">
                                {{ t('imageToVideoAI') }}
                            </div>
                            <p class="feature-description">
                                {{ t('imageToVideoAIDesc') }}
                            </p>
                        </div>
                    </NuxtLink>

                    <NuxtLink to="/text-to-video" class="feature-item">
                        <div class="feature-icon">
                            <svg
                                class="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div class="feature-content_">
                            <div class="feature-title">
                                {{ t('textToVideoAI') }}
                            </div>
                            <p class="feature-description">
                                {{ t('textToVideoAIDesc') }}
                            </p>
                        </div>
                    </NuxtLink>

                    <NuxtLink to="/video-to-video" class="feature-item">
                        <div class="feature-icon">
                            <svg
                                class="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"
                                />
                            </svg>
                        </div>
                        <div class="feature-content_">
                            <div class="feature-title">
                                {{ t('aiVideoEditor') }}
                            </div>
                            <p class="feature-description">
                                {{ t('aiVideoEditorDesc') }}
                            </p>
                        </div>
                    </NuxtLink>
                </div>

                <div class="divider"></div>

                <div class="models-section">
                    <div class="models-title">
                        {{ t('footer.supportedVideoModels') }}
                    </div>
                    <div class="models-grid_">
                        <NuxtLink
                            v-for="model in videoModelList"
                            :key="model.path"
                            :to="model.path"
                            class="model-item"
                            :class="{ hot: model.hot }"
                            :data-hot-label="t('tools.hot')"
                            @click="e => emit('model-click', model, e)"
                            >{{ model.name }}</NuxtLink
                        >
                    </div>
                </div>
            </div>
        </el-popover>

        <!-- Image AI -->
        <el-popover
            placement="bottom"
            trigger="hover"
            popper-class="video-pop"
            :show-arrow="false"
            :popper-style="popStyle"
        >
            <template #reference>
                <NuxtLink to="/ai-image" class="nav-link">
                    {{ t('menus.imageAI') }}
                    <svg
                        class="w-4 h-4 ml-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </NuxtLink>
            </template>
            <div class="dropdown-content">
                <div class="features-section">
                    <NuxtLink to="/text-to-image" class="feature-item">
                        <div class="feature-icon">
                            <svg
                                class="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div class="feature-content_">
                            <div class="feature-title">
                                {{ t('aiImageGenerator') }}
                            </div>
                            <p class="feature-description">
                                {{ t('aiImageGeneratorDesc') }}
                            </p>
                        </div>
                    </NuxtLink>

                    <NuxtLink to="/image-to-image" class="feature-item">
                        <div class="feature-icon">
                            <svg
                                class="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div class="feature-content_">
                            <div class="feature-title">
                                {{ t('imageToImageAI') }}
                            </div>
                            <p class="feature-description">
                                {{ t('imageToImageAIDesc') }}
                            </p>
                        </div>
                    </NuxtLink>
                </div>

                <div class="divider"></div>

                <div class="models-section">
                    <div class="models-title">
                        {{ t('footer.supportedImageModels') }}
                    </div>
                    <div class="models-grid_">
                        <NuxtLink
                            v-for="model in imageModelList"
                            :key="model.path"
                            :to="model.path"
                            class="model-item"
                            :class="{ hot: model.hot }"
                            :data-hot-label="t('tools.hot')"
                            @click="e => emit('model-click', model, e)"
                            >{{ model.name }}</NuxtLink
                        >
                    </div>
                </div>
            </div>
        </el-popover>

        <!-- AI Tools -->
        <el-popover
            placement="bottom"
            trigger="hover"
            popper-class="video-pop"
            :show-arrow="false"
            :popper-style="{
                width: '500px',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '20px',
                padding: '24px',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10)',
            }"
        >
            <template #reference>
                <NuxtLink to="/ai-tools" class="nav-link">
                    {{ t('menus.aiTools') }}
                    <svg
                        class="w-4 h-4 ml-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </NuxtLink>
            </template>
            <div class="dropdown-content tools-dropdown">
                <div class="tools-categories">
                    <div class="tools-column">
                        <div class="tools-header">
                            <div class="tools-icon video-icon">
                                <svg
                                    class="w-5 h-5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"
                                    />
                                </svg>
                            </div>
                            <div class="tools-title">
                                {{ t('footer.videoTools') }}
                            </div>
                        </div>
                        <div class="tools-list">
                            <NuxtLink
                                to="/remove-video-watermark"
                                class="tool-item"
                            >
                                {{ t('aiWatermarkRemoval') }}
                            </NuxtLink>
                            <NuxtLink to="/video-lip-sync" class="tool-item">
                                {{ t('aiVideoLipSync') }}
                            </NuxtLink>
                            <NuxtLink
                                to="/translate-video-subtitle"
                                class="tool-item"
                            >
                                {{ t('tools.videoSubtitleTranslateTitle') }}
                            </NuxtLink>
                            <NuxtLink to="/video-to-anime" class="tool-item">
                                {{ t('tools.videoToAnimeTitle') }}
                            </NuxtLink>
                            <NuxtLink to="/video-upscaler" class="tool-item">
                                {{ t('tools.videoUpscalerTitle') }}
                            </NuxtLink>
                            <NuxtLink
                                to="/remove-video-subtitles"
                                class="tool-item"
                            >
                                {{ t('footer.aiVideoSubtitleErase') }}
                            </NuxtLink>
                            <NuxtLink
                                to="/video-background-remover"
                                class="tool-item"
                            >
                                {{
                                    t('tools.videoBackgroundRemoverShortTitle')
                                }}
                            </NuxtLink>
                            <NuxtLink to="/ai-video-extender" class="tool-item">
                                {{ t('tools.videoExtenderTitle') }}
                            </NuxtLink>
                            <NuxtLink
                                to="/video-motion-imitation"
                                class="tool-item"
                            >
                                {{ t('tools.videoMotionImitationTitle') }}
                            </NuxtLink>
                        </div>
                    </div>

                    <div class="tools-column">
                        <div class="tools-header">
                            <div class="tools-icon image-icon">
                                <svg
                                    class="w-5 h-5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div class="tools-title">
                                {{ t('footer.imageTools') }}
                            </div>
                        </div>
                        <div class="tools-list">
                            <NuxtLink to="/ai-image-resizer" class="tool-item">
                                {{ t('footer.aiImageAspectRatioConverter') }}
                            </NuxtLink>
                            <NuxtLink
                                to="/image-text-translate"
                                class="tool-item"
                            >
                                {{ t('aiImageTranslation') }}
                            </NuxtLink>
                            <NuxtLink
                                to="/ai-background-changer"
                                class="tool-item"
                            >
                                {{ t('footer.aiBackgroundChanger') }}
                            </NuxtLink>
                            <NuxtLink to="/image-upscaler" class="tool-item">
                                {{ t('footer.aiImageUpscaler') }}
                            </NuxtLink>
                            <NuxtLink to="/photo-face-swap" class="tool-item">
                                {{ t('tools.photoFaceSwapTitle') }}
                            </NuxtLink>
                            <NuxtLink
                                to="/remove-text-from-image"
                                class="tool-item"
                            >
                                {{ t('tools.removeTextFromImage') }}
                            </NuxtLink>
                            <NuxtLink to="/photo-restoration" class="tool-item">
                                {{ t('tools.photoRestorationTitle') }}
                            </NuxtLink>
                            <NuxtLink
                                to="/ai-image-background-remover"
                                class="tool-item"
                            >
                                {{ t('img_bg_remove.pageTitle') }}
                            </NuxtLink>
                            <NuxtLink to="/ai-image-extender" class="tool-item">
                                {{ t('img_extender.title') }}
                            </NuxtLink>
                        </div>
                    </div>
                </div>
            </div>
        </el-popover>

        <!-- Effects -->
        <el-popover
            placement="bottom"
            trigger="hover"
            popper-class="video-pop"
            :show-arrow="false"
            :popper-style="{
                width: '180px',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '20px',
                padding: '16px',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10)',
            }"
        >
            <template #reference>
                <span class="nav-link">
                    {{ t('menus.effects') }}
                    <svg
                        class="w-4 h-4 ml-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </span>
            </template>
            <div class="dropdown-content">
                <div class="tools-column">
                    <div class="tools-list">
                        <NuxtLink to="/video-effects" class="tool-item">
                            {{ t('menus.videoEffects') }}
                        </NuxtLink>
                        <NuxtLink to="/photo-effects" class="tool-item">
                            {{ t('menus.photoEffects') }}
                        </NuxtLink>
                    </div>
                </div>
            </div>
        </el-popover>

        <NuxtLink to="/pricing" class="nav-link">{{
            t('menus.pricingLink')
        }}</NuxtLink>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from '@/composables/useI18n'
import type { WebsiteModelNavItem } from '@/utils/website-model-lists'

defineProps<{
    videoModelList: WebsiteModelNavItem[]
    imageModelList: WebsiteModelNavItem[]
}>()

const emit = defineEmits<{
    (e: 'model-click', model: WebsiteModelNavItem, event: MouseEvent): void
}>()

const { t } = useI18n()

const popStyle = {
    width: '500px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '20px',
    padding: '24px',
    backdropFilter: 'blur(6px)',
    border: '1px solid rgba(255, 255, 255, 0.10)',
    boxShadow:
        '0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10)',
} as const
</script>

<style lang="less" scoped>
.nav-menu {
    display: flex;
    align-items: center;
}

.nav-link {
    color: #fff;
    text-decoration: none;
    font-size: 16px;
    font-weight: 400;
    display: flex;
    align-items: center;
    white-space: nowrap;
    transition: color 0.2s ease;
    position: relative;
    padding: 4px 0;
    cursor: pointer;

    &:hover {
        color: var(--weryai-text-active);
    }
}

/* 确保弹窗和触发元素之间的连接 */
::deep(.video-pop) {
    z-index: 2000 !important;
    pointer-events: auto !important;
    margin-top: 8px !important;

    /* 添加一个透明的连接区域，让鼠标可以平滑移动到弹窗 */
    &::before {
        content: '';
        position: absolute;
        top: -8px;
        left: 0;
        right: 0;
        height: 8px;
        background: transparent;
    }
}

::deep(.el-popper.is-pure) {
    pointer-events: auto;
}

.dropdown-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.features-section {
    display: flex;
    flex-direction: row;
    gap: 16px;
    justify-content: space-between;
}

.feature-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 12px;
    transition: all 0.2s ease;
    border-radius: 8px;
    flex: 1;
    text-align: center;
    text-decoration: none;
    color: inherit;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        transform: translateY(-2px);
        text-decoration: none;
        color: inherit;
    }
}

.feature-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #77934e, #447d26);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
}

.feature-content_ {
    width: 100%;
}

.feature-title {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 6px;
    line-height: 1.3;
}

.feature-description {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.4;
}

.divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    margin: 8px 0;
}

.models-section {
    width: 100%;
}

.models-title {
    font-size: 16px;
    font-weight: 600;
    color: white;
    margin-bottom: 16px;
    text-align: center;
}

.models-grid_ {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 8px;
    max-width: 100%;
}

.model-item {
    padding: 6px 12px;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: white;
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    transition: all 0.2s ease;
    position: relative;
    cursor: pointer;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        text-decoration: none;
        color: white;
    }

    &.hot {
        color: var(--weryai-text-active);
        font-weight: 600;

        &:hover {
            color: var(--weryai-text-active);
        }

        &::after {
            content: attr(data-hot-label);
            position: absolute;
            top: -8px;
            right: -8px;
            background: #ef4444;
            color: white;
            font-size: 9px;
            padding: 2px 4px;
            border-radius: 3px;
            font-weight: 600;
        }
    }
}

.tools-dropdown {
    max-width: 600px;
}

.tools-categories {
    display: flex;
    gap: 32px;
    justify-content: space-between;
}

.tools-column {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.tools-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.tools-icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: black;

    &.video-icon {
        background: var(--weryai-text-active);
    }

    &.image-icon {
        background: var(--weryai-text-active);
    }
}

.tools-title {
    font-size: 16px;
    font-weight: 600;
    color: white;
    margin: 0;
}

.tools-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
}

.tool-item {
    color: white;
    text-decoration: none;
    font-size: 14px;
    font-weight: 400;
    padding: 6px 0;
    transition: color 0.2s ease;
    border-radius: 4px;

    &:hover {
        color: var(--weryai-text-active);
        text-decoration: none;
    }
}

@media (max-width: 1280px) {
    .nav-menu {
        display: none;
    }

    .tools-dropdown {
        width: 95vw;
        max-width: none;
    }

    .tools-categories {
        flex-direction: column;
        gap: 20px;
    }

    .tools-header {
        margin-bottom: 12px;
    }

    .tools-title {
        font-size: 15px;
    }

    .tool-item {
        font-size: 13px;
        padding: 4px 0;
    }

    .features-section {
        flex-direction: column;
        gap: 12px;
    }

    .feature-item {
        flex-direction: row;
        align-items: flex-start;
        text-align: left;
        padding: 12px 0;
    }

    .feature-icon {
        width: 32px;
        height: 32px;
    }

    .feature-content_ {
        flex: 1;
    }

    .feature-title {
        font-size: 14px;
    }

    .feature-description {
        font-size: 12px;
    }

    .models-grid_ {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(5, 1fr);
        gap: 6px;
    }

    .model-item {
        padding: 4px 8px;
        font-size: 11px;
    }

    .dropdown-content {
        gap: 24px;
    }
}
</style>
